import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TableParameters } from '../../component-library/table/table-parameters';
import { BackendService } from '../../core/backend.service';
import { ApiResult } from '../../core/messages/api-result';
import { PermissionsService } from '../../core/permissions.service';
import { HbNotification } from '../../notifications/notification';
import { NotificationLife } from '../../notifications/notification-life';
import { NotificationState } from '../../notifications/notification-state';
import { NotificationService } from '../../notifications/notification.service';
import { ItemScenarios } from '../../shared/enums/item-scenarios';
import { GenerateGuidService } from '../../shared/generate-guid/generate-guid.service';
import { TableTemplate } from '../../viewmodels/admin/table-attribute-template/table-template';
import { TableTemplateSaveData } from '../../viewmodels/admin/table-attribute-template/table-template-save-data';
import { TableAttributeTemplateTableService } from './table-attribute-template-table.service';

@Injectable({
  providedIn: 'root',
})
export class TableAttributeTemplateService {
  private selectedTemplateInfo: BehaviorSubject<TableTemplate> = new BehaviorSubject<TableTemplate>(null);
  public selectedTemplateInfo$: Observable<TableTemplate> = this.selectedTemplateInfo.asObservable();
  public scenario: ItemScenarios;
  public templateList: any[] = [];
  public isDeleteAllowed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public isSaveAllowed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public templateInfo: TableTemplate;

  private hasCreatePermission: boolean = false;
  private hasEditPermission: boolean = false;
  private hasDeletePermission: boolean = false;

  public constructor(
    private backendService: BackendService,
    private tableService: TableAttributeTemplateTableService,
    private notificationService: NotificationService,
    private generateGuidService: GenerateGuidService,
    private i18NextPipe: I18NextPipe,
    private permissionsService: PermissionsService,
  ) {
    this.subscribeToPermissionChanges();
  }

  private subscribeToPermissionChanges(): void {
    this.permissionsService.userPermissionKeyValuePairs.subscribe(() => {
      this.assignPermissions();
      this.changeButtonsState();
    });
  }

  private assignPermissions(): void {
    const permissionKeys = this.permissionsService.userPermissionKeys;
    this.hasCreatePermission = this.permissionsService.checkUserHasPermissions(permissionKeys.adminHierarchyTemplateCreate);
    this.hasEditPermission = this.permissionsService.checkUserHasPermissions(permissionKeys.adminHierarchyTemplateUpdate);
    this.hasDeletePermission = this.permissionsService.checkUserHasPermissions(permissionKeys.adminHierarchyTemplateDelete);
  }

  public createNewTemplate(): void {
    this.scenario = ItemScenarios.New;
    const template = new TableTemplate();

    template.tableTemplateId = this.generateGuidService.newGuid();
    this.tableService.setParameter(template, this.canSave());
    this.selectedTemplateInfo.next(template);
    this.templateInfo = template;
    this.tableService.clearColumns();
    this.changeButtonsState();
  }

  public clearTemplate(): void {
    this.selectedTemplateInfo.next(null);
    this.tableService.clearColumns();
    this.isSaveAllowed.next(false);
    this.isDeleteAllowed.next(false);
  }

  private createSaveData(): TableTemplateSaveData {
    const saveDataObj = new TableTemplateSaveData();

    saveDataObj.columns = this.tableService.columns.value;
    saveDataObj.tableTemplateInfo = this.selectedTemplateInfo.value;
    saveDataObj.totalColumnsCount = 0;
    saveDataObj.attributesTableParameters = new TableParameters();

    return saveDataObj;
  }

  public saveTemplate(saveResultCallback: (data: TableTemplateSaveData) => void): void {
    const parameter = this.createSaveData();
    const result = this.scenario === ItemScenarios.New ? this.saveTableTemplate(parameter) : this.updateTableTemplate(parameter);

    result.subscribe(
      data => {
        if (data) {
          this.showCompleteNotification();
          this.loadTableTemplate(data.tableTemplateInfo.tableTemplateId);
          saveResultCallback(data);
        } else {
          this.showFailedNotification();
        }
      },
      (error: ApiResult) => {
        this.showNotification(error.messages[0].text, NotificationState.Failed);
      },
    );
  }

  private saveTableTemplate(data: TableTemplateSaveData): Observable<TableTemplateSaveData> {
    return this.backendService.create<TableTemplateSaveData>(TableTemplateSaveData, data);
  }

  private updateTableTemplate(data: TableTemplateSaveData): Observable<TableTemplateSaveData> {
    return this.backendService.update<TableTemplateSaveData>(TableTemplateSaveData, this.selectedTemplateInfo.value.tableTemplateId, data);
  }

  public showCompleteNotification(): void {
    this.showNotification(this.i18NextPipe.transform('TableAttributeTemplate.SavedMessage'), NotificationState.Complete);
  }

  public showFailedNotification(): void {
    this.showNotification(this.i18NextPipe.transform('TableAttributeTemplate.SaveFailedErrorMessage'), NotificationState.Failed);
  }

  private showNotification(description: string, state: NotificationState): void {
    const notification = new HbNotification();

    notification.description = description;
    notification.state = state;
    notification.life = NotificationLife.Long;
    this.notificationService.displayToast(notification);
  }

  public loadTableTemplate(id: string): void {
    this.scenario = ItemScenarios.Existing;

    this.getTableTemplate(id)
      .pipe(finalize(() => this.changeButtonsState()))
      .subscribe(template => {
        if (template) {
          this.selectedTemplateInfo.next(template);
          this.tableService.setParameter(template, this.canSave());
          this.tableService.loadCollections();
        }
      });
  }

  private getTableTemplate(id: string): Observable<TableTemplate> {
    return this.backendService.getById<TableTemplate>(TableTemplate, id);
  }

  public deleteTableTemplate(templateId: string): Observable<TableTemplate> {
    return this.backendService.delete<TableTemplate>(TableTemplate, templateId);
  }

  public showDeleteCompleteNotification(): void {
    this.showNotification(this.i18NextPipe.transform('TableAttributeTemplate.DeleteSuccessMessage'), NotificationState.Complete);
  }

  public showDeleteFailedNotification(): void {
    this.showNotification(this.i18NextPipe.transform('TableAttributeTemplate.DeleteFailedMessage'), NotificationState.Failed);
  }

  private showDeleteAttributesCompleteNotification(): void {
    this.showNotification(this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.DeleteSuccessMessage'), NotificationState.Complete);
  }

  private showDeleteAttributesFailedNotification(): void {
    this.showNotification(this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.DeleteFailedMessage'), NotificationState.Complete);
  }

  public changeButtonsState(): void {
    this.isSaveAllowed.next(this.canSave());
    this.isDeleteAllowed.next(this.canDelete());
  }

  private canDelete(): boolean {
    const template: TableTemplate = this.selectedTemplateInfo.value;

    return this.scenario === ItemScenarios.Existing && this.hasDeletePermission && template && !!template.tableTemplateId;
  }

  private canSave(): boolean {
    return (this.scenario === ItemScenarios.Existing && this.hasEditPermission) || (this.scenario === ItemScenarios.New && this.hasCreatePermission);
  }

  public deleteAttributes(storedIds: string[], ids: string[]): void {
    this.tableService
      .deleteAttributes(storedIds)
      .pipe()
      .subscribe(
        () => {
          this.showDeleteAttributesCompleteNotification();
          this.tableService.processDeletedAttributes(ids);
        },
        (error: ApiResult) => {
          this.processDeleteAttributesError(error);
        },
      );
  }

  private processDeleteAttributesError(error: ApiResult): void {
    if (error && error.messages && error.messages.length > 0) {
      this.showNotification(error.messages[0].text, NotificationState.Failed);
    } else {
      this.showDeleteAttributesFailedNotification();
    }
  }
}
